'use client'

import { ButtonArrow } from '@/components/ui/button-arrow'
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '@/components/ui/carousel'
import { msg } from '@lingui/core/macro'
import { motion } from 'framer-motion'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { NumberTicker } from '../../fragments/number-ticket'
import { SiteHeadingV2 } from '../../fragments/site-heading-v2'
import { useLingui } from '@lingui/react'

export default function IndustrialTechnologySection() {
  const { i18n } = useLingui()

  const data = [
    {
      id: 'pione-wallet',
      title: msg`Decentralized Wallet`,
      description: msg`A digital asset wallet that enables businesses and users to securely manage, store, and transact digital assets with convenience and safety — serving as the gateway to the global decentralized finance ecosystem`,
      href: 'https://pionewallet.com/',
      image: '/landing/home/pione-wallet.png',
      projectName: msg`PIONE WALLET`
    },
    // {
    //   id: 'pione-superapp',
    //   title: msg`All-in-One Digital Transformation Platform`,
    //   description: msg`An integrated solution providing businesses with digital transformation tools, asset management, transparent payments, and blockchain-based tax invoicing`,
    //   href: '#',
    //   image: '/landing/home/pione-ai.png',
    //   projectName: msg`PIONE SUPER APP`
    // },
    {
      id: 'pione-house',
      title: msg`Real Estate Authentication`,
      description: msg`A blockchain-powered solution for real estate identification and verification, enhancing transparency, reducing risks, and optimizing transaction processes in the property market`,
      href: 'https://pionehouse.com/',
      image: '/landing/home/pione-house.png',
      projectName: msg`PIONE HOUSE`
    },
    {
      id: 'pione-care',
      title: msg`Medical Records Management`,
      description: msg`A blockchain-based personal medical record system that supports healthcare organizations and communities in digitizing data, ensuring information security, and improving healthcare efficiency in the digital age`,
      href: 'https://pionecare.com/',
      image: '/landing/home/pione-care.png',
      projectName: msg`PIONE CARE`
    },

    {
      id: 'pione-farm',
      title: msg`Agricultural Traceability`,
      description: msg`A traceability platform that tracks agricultural products from production to consumption, helping businesses and cooperatives ensure supply chain transparency, strengthen brand value, and build market trust`,
      href: 'https://pionefarm.com/',

      image: '/landing/home/pione-farm.png',
      projectName: msg`PIONE FARM`
    },
    {
      id: 'pione-ai',
      title: msg`AI Virtual Assistant`,
      description: msg`An AI-powered assistant tailored for SMEs, enabling smarter customer service, business management, and operational efficiency.`,
      href: 'https://pioai.net/',

      image: '/landing/home/pione-ai.png',
      projectName: msg`PIONE AI`
    },
    {
      id: 'pione-swap',
      title: msg`Decentralized Exchange (DEX)`,
      description: msg`A decentralized trading platform for security tokens and digital assets, delivering fast, secure, and transparent transactions`,
      href: 'https://pioneswap.com/',
      image: '/landing/home/pione-swap.png',
      projectName: msg`PIONE SWAP`
    },
    {
      id: 'pione-mart',
      title: msg`Decentralized E-Commerce Marketplace`,
      description: msg`A decentralized marketplace that allows digital asset conversion for purchasing goods and services quickly and securely, while supporting seamless cross-border transactions`,
      href: 'https://pionemart.com/',
      image: '/landing/home/pione-mart.png',
      projectName: msg`PIONE MART`
    }
    // {
    //   id: 'pione-zero',
    //   title: msg`Blockchain Infrastructure with zero gas fees`,
    //   description: msg`A next-generation blockchain platform with zero gas fees, accelerating digital transformation and enabling practical applications for enterprises in the AI & Blockchain era`,
    //   href: '#',
    //   image: '/landing/home/pione-zero.png',
    //   projectName: msg`PIONE ZERO`
    // }
  ]

  const [carouselApi, setCarouselApi] = useState<CarouselApi>()
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)

  useEffect(() => {
    if (!carouselApi) {
      return
    }
    const updateSelection = () => {
      setCanScrollPrev(carouselApi.canScrollPrev())
      setCanScrollNext(carouselApi.canScrollNext())
      setCurrentSlide(carouselApi.selectedScrollSnap())
    }
    updateSelection()
    carouselApi.on('select', updateSelection)
    return () => {
      carouselApi.off('select', updateSelection)
    }
  }, [carouselApi])
  return (
    <section className='flex flex-col justify-center h-full bg-white padding-section'>
      <div className='container'>
        <SiteHeadingV2
          title={msg`Pione Ecosystem`}
          description={msg`The Pione Ecosystem is a diverse blockchain ecosystem seamlessly connected within a sustainable network. It comprises multiple interconnected components, all working toward the goal of applying blockchain technology to real-life sectors such as agriculture, healthcare, and decentralized finance.`}
          className='max-w-3xl text-custom'
        />
      </div>
      <div className='relative py-10 mx-auto overflow-hidden w-dvw max-sm:pt-0'>
        <ButtonArrow
          showSvg={false}
          onClick={() => {
            carouselApi?.scrollPrev()
          }}
          disabled={!canScrollPrev}
          className='absolute z-10 p-3 transform -translate-y-1/2 border-0 rounded-full top-1/2 left-4 bg-white/80 hover:bg-white'
        >
          <ArrowLeft className='size-6 text-background/70' />
        </ButtonArrow>
        <ButtonArrow
          showSvg={false}
          onClick={() => {
            carouselApi?.scrollNext()
          }}
          disabled={!canScrollNext}
          className='absolute z-10 p-3 transform -translate-y-1/2 border-0 rounded-full top-1/2 right-4 bg-white/80 hover:bg-white'
        >
          <ArrowRight className='size-6 text-background/70' />
        </ButtonArrow>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className='w-full'
        >
          <Carousel
            setApi={setCarouselApi}
            opts={{
              breakpoints: {
                '(max-width: 768px)': {
                  dragFree: true
                }
              }
            }}
          >
            <CarouselContent className='ml-1'>
              {data.map((item) => (
                <CarouselItem
                  key={item.id}
                  className='max-w-[320px] pl-[20px] py-4 w-full lg:max-w-[390px] overflow-hidden group'
                >
                  <div key={item.id} className={`relative rounded-[30px] overflow-hidden `}>
                    <Image
                      src={item.image || ''}
                      alt={i18n._(item.title)}
                      width={320}
                      height={430}
                      loading='lazy'
                      className='w-[99%] h-full rounded-[32px] object-cover mx-auto'
                    />

                    <h3 className='absolute text-2xl font-bold text-white top-7 md:top-10 left-7 md:text-3xl font-jost'>
                      {i18n._(item.projectName)}
                    </h3>
                    <div className="absolute inset-0 bg-cover bg-no-repeat bg-[url('/landing/home/bg-hover.png')] flex flex-col justify-end p-6 lg:p-7 lg:py-10 overflow-hidden opacity-0 translate-y-20 group-hover:opacity-100 group-hover:translate-y-0 duration-300">
                      <div className='flex flex-col items-start justify-start h-full gap-4 lg:gap-5'>
                        <h3 className='relative text-xl md:text-2xl lg:text-3xl font-bold font-jost text-[#1D1F2A]'>
                          {i18n._(item.title)}
                        </h3>
                        <p className='relative text-base mt-0.5 text-custom'>
                          {i18n._(item.description)}
                        </p>
                      </div>
                      <Link href={item?.href} className='z-10 text-left text-accent-orange'>
                        {i18n._(`Read more`)}→
                      </Link>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          <div className='flex justify-center gap-2 mt-8'>
            {data.map((_, index) => (
              <button
                key={index}
                className={`h-2 w-2 rounded-full transition-colors ${
                  currentSlide === index ? 'bg-accent-blue' : 'bg-black/20'
                }`}
                onClick={() => carouselApi?.scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </motion.div>
      </div>
      <Image
        src='/landing/images/bg-section-line.png'
        width={1900}
        height={231}
        alt='Background Section Line'
        className='object-contain w-full'
      />
      <div className='container py-16 text-center'>
        <motion.h2
          className='max-w-5xl mb-10 text-2xl font-semibold leading-normal text-left md:text-4xl text-custom sm:mb-16 lg:mb-14'
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {i18n._(
            `We are constantly researching and implementing blockchain solutions Breakthrough, highly applicable in real life.`
          )}
        </motion.h2>
        <motion.div
          className="flex items-center gap-5 justify-between w-full bg-no-repeat bg-[url('/landing/home/bg-world.png')]"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className='grid grid-cols-1 gap-20 bg-no-repeat bg-cover sm:grid-cols-2'>
            <div className='flex flex-col items-center gap-3'>
              <span className='text-4xl md:text-5xl font-bold text-[#0ABAE7]'>
                <NumberTicker value={25} />+
              </span>
              <p className='text-2xl font-semibold text-custom font-jost'>
                {i18n._(`Integrated Blockchain & AI Ecosystems`)}
              </p>
            </div>
            <div className='flex flex-col items-center gap-3'>
              <span className='text-4xl md:text-5xl font-bold text-[#16D0B8]'>
                <NumberTicker value={2000} />+
              </span>
              <p className='text-2xl font-semibold text-custom font-jost'>
                {i18n._(`Innovative Ideas Generated Annually`)}
              </p>
            </div>
            <div className='flex flex-col items-center gap-3'>
              <span className='text-4xl md:text-5xl font-bold text-[#F58206]'>
                <NumberTicker value={96} />%
              </span>
              <p className='text-2xl font-semibold text-custom font-jost'>
                {i18n._(`Customer Satisfaction Rate`)}
              </p>
            </div>
            <div className='flex flex-col items-center gap-3'>
              <span className='text-4xl md:text-5xl font-bold text-[#013ADE]'>
                <NumberTicker value={100} />+
              </span>
              <p className='text-2xl font-semibold text-custom font-jost'>{i18n._(`Employees`)}</p>
            </div>
          </div>
          <Image
            src='/landing/home/bg-users.png'
            width={580}
            height={420}
            alt='Background Users'
            className='object-contain max-md:hidden'
          />
        </motion.div>
      </div>
    </section>
  )
}
